import { productTaxonomyRouteCatalog } from './renames';
import type { ProductTaxonomyRouteTemplate, ProductTaxonomyVariant } from './types';

export type { ProductTaxonomyRouteTemplate } from './types';

export type ProductTaxonomyRouteKey = keyof typeof productTaxonomyRouteCatalog;

export function resolveProductTaxonomyRoute(
  key: ProductTaxonomyRouteKey,
  variant: ProductTaxonomyVariant,
): ProductTaxonomyRouteTemplate {
  return productTaxonomyRouteCatalog[key][variant];
}

// Future→current path-fragment replacements derived from the route catalog. Each
// route's templates share the `/workspaces/:workspaceId` prefix and diverge within
// the `/journeys/...` portion, so we operate on that fragment — it appears
// literally in live pathnames, unlike the `:workspaceId` param. Longest fragments
// run first (so nested renames replace before their prefixes) and we dedupe by
// future fragment so the handful of many-to-one renames resolve deterministically.
const JOURNEYS_PATHNAME_CANONICALIZATIONS: { future: string; current: string }[] = (() => {
  const seenFutureFragments = new Set<string>();
  const replacements: { future: string; current: string }[] = [];

  for (const { current, future } of Object.values(productTaxonomyRouteCatalog)) {
    const currentFragment = current.slice(current.indexOf('/journeys'));
    const futureFragment = future.slice(future.indexOf('/journeys'));

    if (futureFragment === currentFragment || seenFutureFragments.has(futureFragment)) {
      continue;
    }

    seenFutureFragments.add(futureFragment);
    replacements.push({ future: futureFragment, current: currentFragment });
  }

  return replacements.sort((a, b) => b.future.length - a.future.length);
})();

/**
 * Canonicalize a journeys pathname to the current (pre-rename) vocabulary.
 *
 * Under the product-taxonomy rename flags, journeys routes are served at their
 * future paths (e.g. `/journeys/automations`, `/journeys/profiles`). Route
 * patterns across the app are written against the current paths
 * (`/journeys/campaigns`, `/journeys/people`), so live pathnames are normalized
 * here before matching. Driven by the route catalog, so every rename — present
 * and future — is covered without per-rename code.
 */
export function normalizeJourneysPathname(pathname: string): string {
  for (const { future, current } of JOURNEYS_PATHNAME_CANONICALIZATIONS) {
    const index = pathname.indexOf(future);

    if (index === -1) {
      continue;
    }

    // Only replace at a segment boundary (end of path or a following `/`), so
    // e.g. `/journeys/reports` never matches inside `/journeys/reports-archive`.
    const nextChar = pathname[index + future.length];
    if (nextChar === undefined || nextChar === '/') {
      return pathname.slice(0, index) + current + pathname.slice(index + future.length);
    }
  }

  return pathname;
}
